import { Button } from '@brightdrop/bd-ui';
import { TranslatedLabel, translateText } from '@gm-commercial/translations';
import { Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import CloseIcon from '../../../../assets/icons/close.svg?react';
import type {
  FilterSummaryProps,
  FilterValueType,
} from './FilterSummary.model';
import useStyles from './FilterSummary.styles';

const FilterSummary = <FilterSet,>(props: FilterSummaryProps<FilterSet>) => {
  const { currentFilterOptions, getChipLabel, onFilterOptionsChange } = props;
  const { classes } = useStyles();

  const deleteSelectedItem = useCallback(
    (name: keyof FilterSet, value: FilterValueType<FilterSet>) => {
      const updatedLabelList = Object.values(
        currentFilterOptions?.[name as keyof FilterSet] || {}
      ).filter((item) => {
        return value !== item;
      });
      const updatedFilterOptions = {
        ...currentFilterOptions,
        [name]: updatedLabelList as FilterValueType<FilterSet>[],
      };
      onFilterOptionsChange(updatedFilterOptions);
    },
    [currentFilterOptions, onFilterOptionsChange]
  );

  const clearAllFilters = useCallback(() => {
    const clearedOptions = Object.keys(currentFilterOptions).reduce(
      (accumulator, key) => {
        const filterKey = key as keyof FilterSet;
        accumulator[filterKey] = [] as unknown as FilterSet[keyof FilterSet];
        return accumulator;
      },
      {} as Partial<FilterSet>
    );
    onFilterOptionsChange(clearedOptions);
  }, [currentFilterOptions, onFilterOptionsChange]);

  const chips = useMemo(() => {
    return currentFilterOptions
      ? Object.entries(currentFilterOptions)
          .reduce(
            (accumulator, [name, values]) => {
              if (!Array.isArray(values)) {
                return accumulator;
              }

              const newItems = (values as FilterValueType<FilterSet>[]).map(
                (value) => ({
                  name: name as keyof FilterSet,
                  label: getChipLabel?.(name as keyof FilterSet, value) ?? '',
                  value,
                })
              );
              return accumulator.concat(newItems);
            },
            [] as {
              name: keyof FilterSet;
              value: FilterValueType<FilterSet>;
              label: string;
            }[]
          )
          ?.map(({ name, label, value }) => (
            <button
              type="button"
              key={`${name.toString()}-${value.toString()}`}
              className={classes.chip}
              data-testid={`clear-${name.toString()}-${value.toString()}-filter`}
              aria-label={`${translateText('common:clearFilter')} ${label}`}
              onClick={() => {
                deleteSelectedItem(name, value);
              }}
            >
              <Typography variant="label_regular">{label}</Typography>
              <CloseIcon className={classes.chipIcon} />
            </button>
          ))
      : [];
  }, [currentFilterOptions, getChipLabel, deleteSelectedItem, classes]);

  return (
    <>
      <TranslatedLabel
        translationKey="common:filters"
        typographyVariant="label_regular"
      />
      {chips}
      {chips.length >= 3 && (
        <Button
          variant="button-link"
          onClick={clearAllFilters}
          data-testid="clear-all-filters-button"
        >
          <TranslatedLabel
            translationKey="common:clearAll"
            typographyVariant="label_regular"
          />
        </Button>
      )}
    </>
  );
};

export default FilterSummary;
